import { useState, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import axios from '../api/axios';
import { isAxiosError } from 'axios';

interface AddUserModalProps {
  onSubmit: () => void;
  onCancel: () => void;
}

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export default function AddUserModal({ onSubmit, onCancel }: AddUserModalProps) {
  const openedAtRef = useRef(Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, control, formState: { errors } } = useForm<RegisterFormData>();
  const password = useWatch({ control, name: 'password' });
  const handleFormSubmit = async (data: RegisterFormData) => {
    try {
      setIsSubmitting(true);
      await axios.post('/auth/register', {
        name: data.name,
        email: data.email,
        password: data.password,
        // This form is only reachable by admins, and its sole purpose is
        // adding moderators (see Sidebar.tsx), so new users default to MODERATOR.
        role: 'MODERATOR'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      onSubmit();
    } catch (error) {
      console.error('Registration error:', error);
      alert(isAxiosError(error) ? error.response?.data?.message ?? 'Wystąpił błąd podczas rejestracji' : 'Wystąpił błąd podczas rejestracji');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      id="add-user-modal"
      className="fixed top-0 left-0 z-[2147483646] flex h-screen w-screen items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Dodaj użytkownika"
    >
      <div
        className="absolute inset-0 bg-black/35"
        onClick={() => {
          if (Date.now() - openedAtRef.current < 260) return;
          onCancel();
        }}
        aria-hidden="true"
      />

      <div
        id="add-user-modal-panel"
        className="relative w-[min(92vw,369px)] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          id="add-user-modal-close"
          type="button"
          className="modal-close-btn absolute top-[8px] right-[8px] z-[2147483647] inline-flex h-6 w-6 items-center justify-center rounded-full border text-white"
          onClick={onCancel}
          aria-label="Zamknij okno dodawania użytkownika"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>

        <h2 id="add-user-modal-title" className="text-2xl font-bold mb-6 text-slate-100 text-center">Dodaj nowego użytkownika</h2>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div>
            <label id="add-user-modal-name-label" className="modal-label">Imię i nazwisko:</label>
            <input
              type="text"
              {...register('name', {
                required: 'Imię i nazwisko jest wymagane'
              })}
              className="modal-input"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label id="add-user-modal-email-label" className="modal-label">Email:</label>
            <input
              type="email"
              {...register('email', {
                required: 'Email jest wymagany',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Nieprawidłowy adres email'
                }
              })}
              className="modal-input"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label id="add-user-modal-password-label" className="modal-label">Hasło:</label>
            <input
              type="password"
              {...register('password', {
                required: 'Hasło jest wymagane',
                minLength: {
                  value: 8,
                  message: 'Hasło musi mieć co najmniej 8 znaków'
                }
              })}
              className="modal-input"
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>
            )}
          </div>

          <div>
            <label className="modal-label">Potwierdź hasło:</label>
            <input
              type="password"
              {...register('confirmPassword', {
                required: 'Potwierdzenie hasła jest wymagane',
                validate: value => value === password || 'Hasła nie są takie same'
              })}
              className="modal-input"
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-400">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="modal-btn-row-mt">
            <button
              id="add-user-modal-cancel"
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="btn-action btn-red"
            >
              Odrzuć
            </button>
            <button
              id="add-user-modal-submit"
              type="submit"
              disabled={isSubmitting}
              className="btn-action btn-green"
            >
              {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
